import React from "react";
import { Link } from "react-router-dom";
import { Move, Shrink, Lock, Plane, Check, X, ShieldCheck, Truck, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { VEYRO, BENEFITS, SPECS, COMPARISON, FAQS, INCLUDED, SHIPPING_INFO, RETURNS_INFO } from "@/lib/veyro";
import { useCart } from "@/context/CartContext";
import { createShopifyCheckout } from "@/lib/shopify";
import { trackPixelEvent } from "@/lib/metaPixel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { Move, Shrink, Lock, Plane };

const Label = ({ children }) => (
  <div className="mono-label text-xs text-[#00E5FF]/90 font-bold">{children}</div>
);

export const TechFrame = ({ src, alt, label, caption, className = "", natural = false }) => (
  <figure className={`rounded-3xl bg-[#0D0F15] border border-[#282D3C] overflow-hidden flex flex-col ${className}`}>
    <div className="flex items-center justify-between px-5 py-3 border-b border-[#282D3C] bg-[#0A0C11]">
      <span className="text-sm font-black tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        VEYRO<span className="text-[#00E5FF]">™</span>
      </span>
      <span className="mono-label text-[10px] text-[#00E5FF]/80 font-bold">{label}</span>
    </div>
    <div className="p-4 sm:p-5 flex-1 flex">
      <div className="rounded-2xl overflow-hidden border border-[#1C202B] bg-[#111319] w-full">
        <img src={src} alt={alt} className={`w-full ${natural ? "h-auto" : "h-full object-cover"}`} loading="lazy" />
      </div>
    </div>
    {caption && <figcaption className="px-5 pb-5 text-base sm:text-lg font-bold text-white leading-snug">{caption}</figcaption>}
  </figure>
);

export const Hero = () => {
  const { price, compareAt } = useCart();
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#090A0E]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,229,255,0.08),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 lg:pt-28 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Label>Veyro™ Compression Series</Label>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.03] text-white">
            Pack More.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00E5FF] to-slate-300">Carry Less.</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-400 max-w-md">
            The luxury travel backpack with built-in vacuum compression. Pack bulky clothing,
            pull the air out, and fit an entire trip into one sleek carry-on.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <Link
              to="/product"
              data-testid="hero-cta-btn"
              className="inline-flex items-center justify-center bg-[#00E5FF] hover:bg-[#33ECFF] text-[#090A0E] rounded-full px-8 py-4 text-base font-bold transition-all duration-200 shadow-[0_0_30px_rgba(0,229,255,0.25)] hover:shadow-[0_0_45px_rgba(0,229,255,0.4)]"
            >
              Shop Now — ${price.toFixed(2)}
            </Link>
            {compareAt && <span className="text-slate-500 line-through text-lg">${compareAt.toFixed(2)}</span>}
          </div>
          <p className="mt-5 text-xs text-slate-500 tracking-wide flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#00E5FF]" /> Secure Checkout • Fast Shipping • Premium Quality
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-3xl overflow-hidden border border-slate-800 shadow-2xl shadow-black/60">
            <img data-testid="hero-image" src={VEYRO.images.black} alt="Veyro vacuum compression travel backpack" className="w-full h-auto max-h-[78vh] object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
};

export const Highlight = () => (
  <section data-testid="highlight-section" className="bg-[#111319] border-y border-slate-800/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div className="rounded-3xl overflow-hidden border border-slate-800">
        <img src={VEYRO.images.navy} alt="Veyro backpack — navy colorway" className="w-full aspect-square object-cover" loading="lazy" />
      </div>
      <div>
        <Label>Product Highlight</Label>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
          Vacuum compression.
          <br />
          Engineered for travel.
        </h2>
        <ul className="mt-8 space-y-4">
          {[
            "Built-in vacuum compression compartment",
            "Expandable, structured carry-on shell",
            "Anti-theft TSA combination lock",
            "Organized storage for every item",
            "Premium materials & padded comfort straps",
          ].map((t, i) => (
            <li key={i} className="flex items-center gap-3 text-slate-300">
              <span className="w-6 h-6 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center shrink-0">
                <Check size={13} className="text-[#00E5FF]" />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const Benefits = () => (
  <section id="features" data-testid="benefits-section" className="bg-[#090A0E]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
      <Label>Why Veyro</Label>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-lg">
        Engineered for people who travel light.
      </h2>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {BENEFITS.map((b, i) => {
          const Icon = ICONS[b.icon];
          return (
            <div key={i} data-testid={`benefit-card-${i}`} className="rounded-2xl bg-[#161922] border border-[#282D3C] p-7 hover:border-[#00E5FF]/40 hover:-translate-y-1 transition-all duration-300">
              <span className="w-11 h-11 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center">
                <Icon size={20} className="text-[#00E5FF]" />
              </span>
              <h3 className="mt-5 text-lg font-semibold tracking-tight text-slate-100">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const Showcase = () => (
  <section data-testid="showcase-section" className="bg-[#090A0E]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-20 lg:pb-32">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-3xl overflow-hidden border border-[#282D3C] bg-[#0D0F15]">
          <img src={VEYRO.images.capacity} alt="Expandable capacity — 28L to 60L equivalent" className="w-full h-auto" loading="lazy" />
        </div>
        <div className="rounded-3xl overflow-hidden border border-[#282D3C] bg-[#0D0F15]">
          <img src={VEYRO.images.lock} alt="Anti-theft TSA lock detail" className="w-full h-auto" loading="lazy" />
        </div>
      </div>
    </div>
  </section>
);

export const ProblemSolution = () => (
  <section data-testid="problem-section" className="bg-[#111319] border-y border-slate-800/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <Label>The Problem</Label>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
          Overpacked luggage slows you down.
        </h2>
        <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
          Bulky suitcases. Checked-bag fees. Dragging a trolley through terminals and
          cobblestones. Traditional luggage fights the way you actually travel.
        </p>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
          Veyro compresses your clothing inside the bag itself — a whole trip on your back,
          carry-on friendly, with both hands free from curb to gate.
        </p>
      </div>
      <div className="rounded-3xl overflow-hidden border border-[#282D3C] bg-[#0D0F15]">
        <img src={VEYRO.images.howto} alt="How to use the vacuum compression bag" className="w-full h-auto" loading="lazy" />
      </div>
    </div>
  </section>
);

export const Steps = () => (
  <section data-testid="steps-section" className="bg-[#090A0E]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
      <Label>How It Works</Label>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Three steps to a lighter trip.</h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          ["Pack it", "Fold clothing into the built-in vacuum compression compartment."],
          ["Compress it", "Seal and remove the air — bulky items flatten by up to half."],
          ["Go", "Zip up, lock it, and carry a whole trip in one sleek backpack."],
        ].map(([t, d], i) => (
          <div key={i} data-testid={`step-card-${i}`} className="rounded-2xl bg-[#161922] border border-[#282D3C] p-8">
            <span className="mono-label text-2xl font-bold text-[#00E5FF]">0{i + 1}</span>
            <h3 className="mt-4 text-xl font-semibold text-white">{t}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Specs = () => (
  <section id="specs" data-testid="specs-section" className="bg-[#111319] border-y border-slate-800/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
      <Label>Specifications</Label>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Built to exact standards.</h2>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#282D3C] rounded-2xl overflow-hidden border border-[#282D3C]">
        {SPECS.map((s, i) => (
          <div key={i} data-testid={`spec-row-${i}`} className="bg-[#0D0F15] p-7">
            <div className="mono-label text-[11px] text-slate-500 font-bold">{s.label}</div>
            <div className="mt-2 text-lg font-semibold text-slate-200">{s.value}</div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-600">Additional specifications can be added anytime.</p>
    </div>
  </section>
);

export const Comparison = () => (
  <section data-testid="comparison-section" className="bg-[#090A0E]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
      <Label>Comparison</Label>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Veyro vs. traditional luggage.</h2>
      <div className="mt-12 rounded-2xl border border-[#282D3C] overflow-hidden">
        <div className="grid grid-cols-3 bg-[#161922] text-sm font-bold text-white">
          <div className="p-4 sm:p-5">Feature</div>
          <div className="p-4 sm:p-5 text-center text-[#00E5FF]">VEYRO™</div>
          <div className="p-4 sm:p-5 text-center text-slate-500">Traditional</div>
        </div>
        {COMPARISON.map((row, i) => (
          <div key={i} data-testid={`compare-row-${i}`} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-[#0D0F15]" : "bg-[#0A0C11]"}`}>
            <div className="p-4 sm:p-5 text-slate-300">{row.feature}</div>
            <div className="p-4 sm:p-5 flex justify-center">
              {row.veyro ? <Check size={18} className="text-[#00E5FF]" /> : <X size={18} className="text-slate-600" />}
            </div>
            <div className="p-4 sm:p-5 flex justify-center">
              {row.traditional ? <Check size={18} className="text-slate-400" /> : <X size={18} className="text-slate-600" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FAQ = () => (
  <section id="faq" data-testid="faq-section" className="bg-[#090A0E]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-32">
      <Label>FAQ</Label>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Questions, answered.</h2>
      <Accordion type="single" collapsible className="mt-10">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-[#282D3C]">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-slate-200 hover:text-white hover:no-underline">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const FinalCTA = () => {
  const { addToCart, price, compareAt, variantId } = useCart();
  const [buying, setBuying] = React.useState(false);

  const buyNow = async () => {
    setBuying(true);
    trackPixelEvent("InitiateCheckout", {
      content_name: VEYRO.name,
      content_type: "product",
      contents: [{ id: VEYRO.pixelId, quantity: 1 }],
      num_items: 1,
      value: price,
      currency: "USD",
    });
    try {
      if (!variantId) {
        toast.error("Checkout is being connected — please try again in a little while.");
        setBuying(false);
        return;
      }
      const url = await createShopifyCheckout([{ merchandiseId: variantId, quantity: 1 }]);
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setBuying(false);
    }
  };

  return (
    <section data-testid="final-cta-section" className="relative bg-[#111319] border-t border-slate-800/60 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,229,255,0.08),transparent_60%)]" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-24 lg:py-36 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">
          Pack More. <span className="text-[#00E5FF]">Carry Less.</span>
        </h2>
        <p data-testid="final-cta-product-name" className="mt-5 text-lg sm:text-xl font-bold text-white">{VEYRO.name}</p>
        <div className="mt-3 flex items-center justify-center gap-3">
          <span data-testid="final-cta-price" className="text-3xl font-black tracking-tight text-white">${price.toFixed(2)}</span>
          {compareAt && compareAt > price && (
            <span className="text-lg text-slate-500 line-through">${compareAt.toFixed(2)}</span>
          )}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            data-testid="final-cta-add-to-cart-btn"
            onClick={() => addToCart(1)}
            className="w-full sm:w-auto inline-flex items-center justify-center bg-[#00E5FF] hover:bg-[#33ECFF] text-[#090A0E] rounded-full px-10 py-4 text-lg font-bold transition-all duration-200 shadow-[0_0_35px_rgba(0,229,255,0.3)]"
          >
            Add to Cart
          </button>
          <button
            data-testid="final-cta-buy-now-btn"
            onClick={buyNow}
            disabled={buying}
            className="w-full sm:w-auto inline-flex items-center justify-center bg-white hover:bg-slate-200 text-[#090A0E] rounded-full px-10 py-4 text-lg font-bold transition-colors duration-200 disabled:opacity-70"
          >
            {buying ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Redirecting...</span> : "Buy Now"}
          </button>
        </div>
        <div data-testid="final-cta-included" className="mt-10 mx-auto max-w-md rounded-2xl bg-[#0D0F15]/80 border border-[#282D3C] p-6 text-left">
          <div className="mono-label text-[11px] text-[#00E5FF] font-bold">What's Included</div>
          <ul className="mt-3 space-y-2.5 text-sm text-slate-200">
            {INCLUDED.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Check size={15} className="text-[#00E5FF] mt-0.5 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6 space-y-1.5 text-sm text-slate-400">
          <div className="flex items-center justify-center gap-2"><Truck size={15} className="text-[#00E5FF]" /> {SHIPPING_INFO}</div>
          <div className="flex items-center justify-center gap-2"><RotateCcw size={15} className="text-[#00E5FF]" /> {RETURNS_INFO}</div>
        </div>
      </div>
    </section>
  );
};
